import React, { useEffect, useId, useState } from "react";
import toast from "react-hot-toast";
import { X, ShieldCheck, AlertTriangle } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { Input } from "@/components/ui/Input";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { CONFIRM_INPUT_CLASS } from "../constants";
import CodeBlock from "./CodeBlock";
import CountTile from "./CountTile";
import ErrorNote from "./ErrorNote";

/**
 * PermissionsSyncModal — mirrors RlsApplyModal in shape and confirmation
 * flow.
 *
 * ``row`` shape: the PermissionsDatabaseStatus payload (or the synthetic
 * ``{ key: 'all', isSyntheticAll: true, label: '...' }`` for fleet-wide).
 *
 * The synced diff has three axes (added / updated / removed). For a
 * single-target open we auto-load the preview so the SuperAdmin sees
 * exactly which permission codes will change. For 'all' the diff differs
 * per DB, so preview is skipped and the modal jumps straight to the
 * confirmation gate.
 */
const PermissionsSyncModal = ({ row, onClose, onCompleted }) => {
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewStatements, setPreviewStatements] = useState([]);
    const [previewCounts, setPreviewCounts] = useState({
        added: 0,
        updated: 0,
        removed: 0,
    });
    const [confirmText, setConfirmText] = useState("");
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState(null);
    const confirmId = useId();

    // API payload is camelCase (tenantId, tenantSlug). ``key`` matches
    // tenant_id for dedicated rows so it's a safe fallback.
    const target = row?.isSyntheticAll ? "all" : `tenant:${row?.tenantId ?? row?.key ?? ""}`;
    const isAll = row?.isSyntheticAll;
    // Same-shape confirmation gate as RlsApplyModal — type the tenant slug
    // (or "APPLY ALL") to unlock the Apply button.
    const expectedConfirm = isAll ? "APPLY ALL" : row?.tenantSlug || "";

    useEffect(() => {
        if (!row || isAll) return;
        let cancelled = false;
        (async () => {
            try {
                setPreviewLoading(true);
                setError(null);
                const data = await PlatformService.previewPermissionsSync({ target });
                if (cancelled) return;
                setPreviewStatements(data?.statements ?? []);
                setPreviewCounts({
                    added: data?.added ?? 0,
                    updated: data?.updated ?? 0,
                    removed: data?.removed ?? 0,
                });
            } catch (err) {
                if (!cancelled) setError(apiErrorMessageFormat(err));
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [row, target, isAll]);

    const canApply =
        confirmText.trim() === expectedConfirm &&
        expectedConfirm.length > 0 &&
        !applying;

    const onApplyClick = async () => {
        if (!canApply) return;
        try {
            setApplying(true);
            setError(null);
            const data = await PlatformService.applyPermissionsSync({ target });
            const succeeded = (data?.results ?? []).filter((r) => r.success).length;
            const failed = (data?.results ?? []).filter((r) => !r.success).length;
            if (failed > 0) {
                toast.error(
                    `${failed} database${failed > 1 ? "s" : ""} failed. Check the platform audit log for the reason.`
                );
            } else {
                toast.success(
                    `Permissions synced to ${succeeded} database${succeeded === 1 ? "" : "s"}.`
                );
            }
            onCompleted?.();
        } catch (err) {
            setError(apiErrorMessageFormat(err));
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && !applying && onClose?.()}>
            <DialogContent className="w-[340px] md:w-[720px] max-h-[85vh] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary">
                        Sync permissions
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
                        Target:{" "}
                        <span className="font-medium">
                            {row?.label || row?.tenantSlug || target}
                        </span>
                    </DialogDescription>
                    <DialogClose
                        disabled={applying}
                        aria-label="Close"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X size={16} strokeWidth={3} aria-hidden="true" />
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                    {isAll ? (
                        <Callout tone="warning" icon={AlertTriangle} size="xs">
                            <span className="font-medium block mb-0.5">
                                No preview for a fleet-wide sync
                            </span>
                            Each dedicated database has its own diff. Preview a single
                            one from its row to see the exact changes for that tenant.
                        </Callout>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <CountTile
                                    label="To add"
                                    value={previewCounts.added}
                                    loading={previewLoading}
                                    colorClass="bg-tone-emerald-soft text-tone-emerald"
                                />
                                <CountTile
                                    label="To update"
                                    value={previewCounts.updated}
                                    loading={previewLoading}
                                    colorClass="bg-tone-amber-soft text-tone-amber"
                                />
                                <CountTile
                                    label="To remove"
                                    value={previewCounts.removed}
                                    loading={previewLoading}
                                    colorClass="bg-tone-rose-soft text-tone-rose"
                                />
                            </div>
                            <div>
                                <div className="text-xs font-medium text-text-secondary mb-2">
                                    SQL that Sync will run
                                </div>
                                <CodeBlock>
                                    {previewLoading ? (
                                        <Skeleton className="h-24 w-full" />
                                    ) : previewStatements.length === 0 ? (
                                        <span className="text-text-muted">
                                            No changes — this database already carries the
                                            platform catalog.
                                        </span>
                                    ) : (
                                        previewStatements.map((stmt, i) => (
                                            <pre key={i} className="whitespace-pre-wrap mb-2">
                                                {stmt}
                                            </pre>
                                        ))
                                    )}
                                </CodeBlock>
                            </div>
                        </>
                    )}

                    <div>
                        <label
                            htmlFor={confirmId}
                            className="block text-xs text-text-secondary mb-1"
                        >
                            Type{" "}
                            <span className="font-mono font-semibold text-text-primary">
                                {expectedConfirm || "the tenant slug"}
                            </span>{" "}
                            to confirm
                        </label>
                        <Input
                            id={confirmId}
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            disabled={applying}
                            className={CONFIRM_INPUT_CLASS}
                            placeholder={expectedConfirm}
                            autoComplete="off"
                        />
                    </div>

                    {error && <ErrorNote>{error}</ErrorNote>}
                </div>

                <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
                    <DialogClose
                        disabled={applying}
                        render={
                            <Button variant="outline" type="button" disabled={applying}>
                                Cancel
                            </Button>
                        }
                    />
                    <Button
                        onClick={onApplyClick}
                        disabled={!canApply}
                        className="bg-button-secondary text-text-base"
                    >
                        <ShieldCheck size={14} className="mr-1" aria-hidden="true" />
                        {applying ? "Syncing…" : "Sync"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PermissionsSyncModal;
